import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { IndianRupee, Percent, ShieldCheck, Wallet, X } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { formatCurrency } from '@/lib/utils';
import { dashboardApi } from '../api/dashboard.api';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const fmtDate = (d: string) =>
  new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

export function RevenueBreakdownModal({ isOpen, onClose }: Props) {
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['revenue-breakdown', page],
    queryFn: () => dashboardApi.getRevenueBreakdown({ page, limit: 20 }),
    enabled: isOpen,
  });

  const s = data?.summary;
  const cards = [
    { label: 'Commission', value: s?.commission, icon: Percent, color: 'text-blue-600' },
    { label: 'Platform Fee', value: s?.platform_fee, icon: IndianRupee, color: 'text-violet-600' },
    { label: 'Bookfleet Shield', value: s?.shield, icon: ShieldCheck, color: 'text-emerald-600' },
    { label: 'Bookfleet Revenue', value: s?.total, icon: Wallet, color: 'text-amber-600' },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-5xl">
      <div className="flex items-center justify-between border-b px-5 py-4">
        <div>
          <h2 className="text-lg font-semibold">Bookfleet Revenue Breakdown</h2>
          <p className="text-xs text-muted-foreground">
            Commission + Platform Fee + Shield over completed orders (GST and driver earnings excluded)
          </p>
        </div>
        <Button variant="ghost" size="icon-sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : (
        <div className="max-h-[75vh] overflow-y-auto p-5">
          {/* Summary cards */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {cards.map((c) => (
              <div key={c.label} className="rounded-lg border p-4">
                <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                  <c.icon className={`h-4 w-4 ${c.color}`} />
                  {c.label}
                </div>
                <div className="mt-2 text-xl font-bold">{formatCurrency(c.value ?? 0)}</div>
              </div>
            ))}
          </div>

          <p className="mt-3 text-xs text-muted-foreground">
            {s?.order_count ?? 0} completed orders · GST collected {formatCurrency(s?.gst ?? 0)} ·
            {' '}Driver earnings {formatCurrency(s?.driver_earnings ?? 0)}
          </p>

          {/* Per-order table */}
          <div className="mt-4 rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Fare</TableHead>
                  <TableHead className="text-right">Commission</TableHead>
                  <TableHead className="text-right">Platform</TableHead>
                  <TableHead className="text-right">Shield</TableHead>
                  <TableHead className="text-right">GST</TableHead>
                  <TableHead className="text-right">Driver Earnings</TableHead>
                  <TableHead className="text-right">Bookfleet Revenue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!data?.orders?.length ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      No completed orders yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.orders.map((o) => (
                    <TableRow key={o.booking_id}>
                      <TableCell className="whitespace-nowrap text-sm">{fmtDate(o.created_at)}</TableCell>
                      <TableCell>{o.customer_name || 'Unknown'}</TableCell>
                      <TableCell className="text-right">{formatCurrency(o.final_fare)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(o.commission)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(o.platform_fee)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(o.shield)}</TableCell>
                      <TableCell className="text-right text-rose-600">{formatCurrency(o.gst)}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{formatCurrency(o.driver_earnings)}</TableCell>
                      <TableCell className="text-right font-semibold text-amber-600">
                        {formatCurrency(o.zipto_revenue)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {data && data.total_pages > 1 && (
            <div className="mt-4 flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Prev
              </Button>
              <span className="text-sm text-muted-foreground">Page {page} of {data.total_pages}</span>
              <Button variant="outline" size="sm" disabled={page >= data.total_pages} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
